/** Resolve operating-country / picker flag URLs (relative media paths, flagcdn fallback). */

function apiBaseURL() {
  try {
    // eslint-disable-next-line global-require, import/no-unresolved
    const configService = require("@/services/config").default;
    const fromConfig = configService?.get?.("baseURL");
    if (fromConfig) return String(fromConfig);
  } catch {
    /* unit / Storybook without account config */
  }
  return process.env.VUE_APP_BASE_URL || "";
}

export function resolveFlagUrl(flagIcon) {
  if (!flagIcon) return "";
  const s = String(flagIcon).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:")) {
    return s;
  }
  const base = apiBaseURL();
  if (!base) return s;
  if (s.startsWith("/") && base.endsWith("/")) {
    return `${base.slice(0, -1)}${s}`;
  }
  if (!s.startsWith("/") && !base.endsWith("/")) {
    return `${base}/${s}`;
  }
  return `${base}${s}`;
}

export function flagcdnFromCode(code) {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (!c || c.length !== 2) return "";
  return `https://flagcdn.com/${c}.svg`;
}

/**
 * ISO 3166-1 alpha-3 → alpha-2 for every country/territory whose flag is
 * self-hosted by the backend as `/static/images/flags/{alpha3}.svg`.
 * flagcdn.com is keyed by alpha-2, so the full catalog's alpha-3 flag filename
 * has to be translated client-side (ER-11939).
 */
export const ISO_ALPHA2_BY_ALPHA3 = {
  abw: "aw",
  afg: "af",
  ago: "ao",
  aia: "ai",
  alb: "al",
  and: "ad",
  are: "ae",
  arg: "ar",
  arm: "am",
  asm: "as",
  atg: "ag",
  aus: "au",
  aze: "az",
  bdi: "bi",
  bel: "be",
  ben: "bj",
  bfa: "bf",
  bgd: "bd",
  bgr: "bg",
  bhr: "bh",
  bhs: "bs",
  bih: "ba",
  blm: "bl",
  blr: "by",
  blz: "bz",
  bmu: "bm",
  bol: "bo",
  bra: "br",
  brb: "bb",
  brn: "bn",
  btn: "bt",
  bwa: "bw",
  caf: "cf",
  can: "ca",
  che: "ch",
  chl: "cl",
  chn: "cn",
  civ: "ci",
  cmr: "cm",
  cod: "cd",
  cog: "cg",
  cok: "ck",
  col: "co",
  com: "km",
  cpv: "cv",
  cri: "cr",
  cub: "cu",
  cym: "ky",
  cyp: "cy",
  cze: "cz",
  deu: "de",
  dji: "dj",
  dma: "dm",
  dnk: "dk",
  dom: "do",
  dza: "dz",
  ecu: "ec",
  egy: "eg",
  eri: "er",
  esp: "es",
  est: "ee",
  eth: "et",
  fin: "fi",
  fji: "fj",
  flk: "fk",
  fra: "fr",
  fro: "fo",
  fsm: "fm",
  gab: "ga",
  gbr: "gb",
  geo: "ge",
  gha: "gh",
  gib: "gi",
  gin: "gn",
  glp: "gp",
  gmb: "gm",
  gnb: "gw",
  gnq: "gq",
  grc: "gr",
  grd: "gd",
  grl: "gl",
  gtm: "gt",
  guf: "gf",
  gum: "gu",
  guy: "gy",
  hkg: "hk",
  hnd: "hn",
  hrv: "hr",
  hti: "ht",
  hun: "hu",
  idn: "id",
  ind: "in",
  iot: "io",
  irl: "ie",
  irn: "ir",
  irq: "iq",
  isl: "is",
  isr: "il",
  ita: "it",
  jam: "jm",
  jor: "jo",
  jpn: "jp",
  kaz: "kz",
  ken: "ke",
  kgz: "kg",
  khm: "kh",
  kir: "ki",
  kna: "kn",
  kor: "kr",
  kos: "xk",
  kwt: "kw",
  lao: "la",
  lbn: "lb",
  lbr: "lr",
  lby: "ly",
  lca: "lc",
  lie: "li",
  lka: "lk",
  lso: "ls",
  ltu: "lt",
  lux: "lu",
  lva: "lv",
  mac: "mo",
  maf: "mf",
  mar: "ma",
  mco: "mc",
  mda: "md",
  mdg: "mg",
  mdv: "mv",
  mex: "mx",
  mhl: "mh",
  mkd: "mk",
  mli: "ml",
  mlt: "mt",
  mmr: "mm",
  mne: "me",
  mng: "mn",
  mnp: "mp",
  moz: "mz",
  mrt: "mr",
  msr: "ms",
  mtq: "mq",
  mus: "mu",
  mwi: "mw",
  mys: "my",
  myt: "yt",
  nam: "na",
  ncl: "nc",
  ner: "ne",
  nfk: "nf",
  nga: "ng",
  nic: "ni",
  niu: "nu",
  nld: "nl",
  nor: "no",
  npl: "np",
  nru: "nr",
  nzl: "nz",
  omn: "om",
  pak: "pk",
  pan: "pa",
  per: "pe",
  phl: "ph",
  plw: "pw",
  png: "pg",
  pol: "pl",
  pri: "pr",
  prk: "kp",
  prt: "pt",
  pry: "py",
  pse: "ps",
  pyf: "pf",
  qat: "qa",
  reu: "re",
  rou: "ro",
  rus: "ru",
  rwa: "rw",
  sau: "sa",
  sdn: "sd",
  sen: "sn",
  sgp: "sg",
  shn: "sh",
  slb: "sb",
  sle: "sl",
  slv: "sv",
  smr: "sm",
  som: "so",
  spm: "pm",
  srb: "rs",
  stp: "st",
  sur: "sr",
  svk: "sk",
  svn: "si",
  swe: "se",
  swz: "sz",
  syc: "sc",
  syr: "sy",
  tca: "tc",
  tgo: "tg",
  tha: "th",
  tjk: "tj",
  tkl: "tk",
  tkm: "tm",
  tls: "tl",
  ton: "to",
  tto: "tt",
  tun: "tn",
  tur: "tr",
  tuv: "tv",
  twn: "tw",
  tza: "tz",
  uga: "ug",
  ukr: "ua",
  ury: "uy",
  usa: "us",
  uzb: "uz",
  vat: "va",
  vct: "vc",
  ven: "ve",
  vgb: "vg",
  vir: "vi",
  vnm: "vn",
  vut: "vu",
  wlf: "wf",
  yem: "ye",
  zaf: "za",
  zmb: "zm",
  zwe: "zw",
};

/** ISO alpha-3 → lowercase alpha-2, or "" when the code is unknown. */
export function alpha2FromAlpha3(alpha3) {
  const key = String(alpha3 || "")
    .trim()
    .toLowerCase();
  return ISO_ALPHA2_BY_ALPHA3[key] || "";
}

/**
 * flagcdn URL derived from a self-hosted `/static/images/flags/{alpha3}.svg`
 * path (ER-11939). Returns "" when the path is not a recognised alpha-3 flag
 * filename, so callers can fall back to the self-hosted URL.
 */
export function flagcdnFromFlagIcon(flagIcon) {
  const s = String(flagIcon || "").trim();
  if (!s) return "";
  const match = s.match(/(?:^|\/)([a-z]{3})\.svg$/i);
  if (!match) return "";
  return flagcdnFromCode(alpha2FromAlpha3(match[1]));
}

/** True when `icon` is an emoji/text fallback, not a media URL or path. */
export function isEmojiIcon(icon) {
  if (!icon || typeof icon !== "string") return false;
  const s = icon.trim();
  if (!s) return false;
  if (/^https?:\/\//i.test(s) || s.startsWith("data:")) return false;
  if (s.includes("/") || /\.(svg|png|jpe?g|webp|gif)$/i.test(s)) return false;
  return true;
}

/**
 * Best flag image URL for a country row (Figma / Operating Country table parity).
 * flagcdn (keyed by the ISO-2 `code` field) is the primary source; the resolved
 * API media path is kept as a last-resort fallback for rows without a code
 * (ER-11927).
 */
export function resolveCountryFlagIcon(source = {}) {
  const code = source.code;
  const raw = source.icon || source.flag_icon || source.flag || "";
  const fromCode = flagcdnFromCode(code);
  if (fromCode) return fromCode;
  if (raw && !isEmojiIcon(raw)) {
    const resolved = resolveFlagUrl(raw);
    if (resolved) return resolved;
  }
  return "";
}

/**
 * flagcdn fallback URL to use when a country flag's primary image fails to load.
 * Returns "" when no 2-letter ISO code is available — the caller should then hide
 * the <img> rather than render a broken icon (ER-11925).
 */
export function resolveCountryFlagFallback(source = {}) {
  return flagcdnFromCode(source?.code);
}
